import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  getPayments, 
  createPayment, 
  createSplitPayment,
  getClients, 
  getInvoices,
  getPaymentAllocations,
  allocatePayment,
  removePaymentAllocation,
  getPaymentMethods,
  getPaymentReceiptPdf,
  getClientWallet
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  Plus, 
  CreditCard, 
  Save, 
  Split,
  Trash2,
  Check,
  AlertCircle,
  Download,
  Wallet,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';
import SmartSearch from '@/components/SmartSearch';

const defaultPaymentMethods = ['Cash', 'Card', 'Bank Transfer', 'Cheque', 'Online', 'Wallet'];

// Column definitions for smart search
const searchColumns = [
  { key: 'receipt_number', label: 'Receipt #', type: 'text' },
  { key: 'client_name', label: 'Client', type: 'text' },
  { key: 'payment_method', label: 'Method', type: 'select', options: defaultPaymentMethods.map(m => ({ value: m, label: m })) },
  { key: 'amount', label: 'Amount', type: 'number' },
  { key: 'payment_date', label: 'Date', type: 'date' },
  { key: 'reference_number', label: 'Reference', type: 'text' },
  { key: 'notes', label: 'Notes', type: 'text' },
];

// Quick filter presets
const quickFilters = [
  { label: 'Cash', filters: { payment_method: 'Cash' } },
  { label: 'Card', filters: { payment_method: 'Card' } },
  { label: 'Bank Transfer', filters: { payment_method: 'Bank Transfer' } },
  { label: 'Today', filters: { payment_date: 'today' } },
  { label: 'This Month', filters: { payment_date: 'thisMonth' } },
];

export default function PaymentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('invoice_id');

  const [allPayments, setAllPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(!!invoiceId);
  const [clients, setClients] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState(defaultPaymentMethods);
  const [downloading, setDownloading] = useState(null);
  const [clientWallet, setClientWallet] = useState(null);
  
  // Split payment state
  const [paymentMode, setPaymentMode] = useState('single'); // 'single' or 'split'
  const [paymentSplits, setPaymentSplits] = useState([
    { payment_method: 'Wallet', amount: 0, reference_number: '' }
  ]);
  
  // Allocation state
  const [allocationSheetOpen, setAllocationSheetOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [allocationAmounts, setAllocationAmounts] = useState({});

  const [newPayment, setNewPayment] = useState({
    client_id: '',
    invoice_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    amount: 0,
    payment_method: 'Cash',
    reference_number: '',
    bank_name: '',
    notes: '',
    allocate_to_invoices: []
  });

  useEffect(() => {
    loadPayments();
    loadClients();
    loadPaymentMethods();
  }, []);

  useEffect(() => {
    if (newPayment.client_id) {
      loadClientInvoices(newPayment.client_id);
      loadClientWallet(newPayment.client_id);
    } else {
      setClientWallet(null);
    }
  }, [newPayment.client_id]);

  useEffect(() => {
    if (invoiceId) {
      loadInvoiceDetails(invoiceId);
    }
  }, [invoiceId]);

  // Auto-suggest wallet usage when wallet has balance and invoice is selected
  useEffect(() => {
    if (clientWallet && clientWallet.balance > 0 && newPayment.amount > 0 && paymentMode === 'single') {
      // If wallet can cover full amount
      if (clientWallet.balance >= newPayment.amount) {
        // Keep current method, user can choose
      } else {
        // Suggest split payment
        // Don't auto-switch, let user decide
      }
    }
  }, [clientWallet, newPayment.amount, paymentMode]);

  const loadPayments = async () => {
    try {
      const res = await getPayments({ limit: 1000 });
      setAllPayments(res.data.items || []);
      setFilteredPayments(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const res = await getClients({ limit: 1000 });
      setClients(res.data.items || []);
    } catch (error) {
      console.error(error);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const res = await getPaymentMethods();
      if (res.data && res.data.length > 0) {
        const methods = res.data.map(m => m.name);
        if (!methods.includes('Wallet')) {
          methods.push('Wallet');
        }
        setPaymentMethods(methods);
      }
    } catch (error) {
      console.error('Using default payment methods');
    }
  };

  const loadClientInvoices = async (clientId) => {
    try {
      const res = await getInvoices({ client_id: clientId, limit: 100 });
      const unpaidInvoices = (res.data.items || []).filter(inv => inv.balance_due > 0);
      setClientInvoices(unpaidInvoices);
    } catch (error) {
      console.error(error);
    }
  };

  const loadClientWallet = async (clientId) => {
    try {
      const res = await getClientWallet(clientId);
      setClientWallet(res.data);
    } catch (error) {
      console.error('Failed to load wallet', error);
      setClientWallet(null);
    }
  };

  const loadInvoiceDetails = async (invId) => {
    try {
      const res = await getInvoices({ limit: 1000 });
      const inv = (res.data.items || []).find(i => i.id === invId);
      if (inv) {
        setNewPayment(prev => ({
          ...prev,
          client_id: inv.client_id,
          invoice_id: invId,
          amount: inv.balance_due
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handlePaymentChange = (field, value) => {
    const actualValue = (field === 'invoice_id' && value === 'none') ? '' : value;
    setNewPayment((prev) => ({ ...prev, [field]: actualValue }));

    if (field === 'invoice_id' && actualValue && actualValue !== 'none') {
      const inv = clientInvoices.find(i => i.id === actualValue);
      if (inv) {
        setNewPayment((prev) => ({ ...prev, invoice_id: actualValue, amount: inv.balance_due }));
        // Reset splits when invoice changes
        if (clientWallet && clientWallet.balance > 0) {
          const walletAmount = Math.min(clientWallet.balance, inv.balance_due);
          const remaining = inv.balance_due - walletAmount;
          if (remaining > 0) {
            setPaymentSplits([
              { payment_method: 'Wallet', amount: walletAmount, reference_number: '' },
              { payment_method: 'Cash', amount: remaining, reference_number: '' }
            ]);
          } else {
            setPaymentSplits([
              { payment_method: 'Wallet', amount: walletAmount, reference_number: '' }
            ]);
          }
        }
      }
    }
  };

  const handleSave = async () => {
    if (!newPayment.client_id || !newPayment.amount) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      if (paymentMode === 'split' && paymentSplits.length > 1) {
        // Process split payment
        const validSplits = paymentSplits.filter(s => s.amount > 0);
        if (validSplits.length === 0) {
          toast.error('Please add at least one payment split with amount');
          setSaving(false);
          return;
        }

        const splitsTotal = validSplits.reduce((sum, s) => sum + s.amount, 0);
        if (Math.abs(splitsTotal - newPayment.amount) > 0.01) {
          toast.error(`Split amounts (${formatCurrency(splitsTotal)}) must equal total (${formatCurrency(newPayment.amount)})`);
          setSaving(false);
          return;
        }

        // Check wallet balance for wallet splits
        const walletSplitAmount = validSplits
          .filter(s => s.payment_method === 'Wallet')
          .reduce((sum, s) => sum + s.amount, 0);
        
        if (walletSplitAmount > (clientWallet?.balance || 0)) {
          toast.error(`Insufficient wallet balance. Available: ${formatCurrency(clientWallet?.balance || 0)}`);
          setSaving(false);
          return;
        }

        await createSplitPayment({
          client_id: newPayment.client_id,
          invoice_id: newPayment.invoice_id || undefined,
          payment_date: newPayment.payment_date,
          total_amount: newPayment.amount,
          splits: validSplits.map(s => ({
            payment_method: s.payment_method,
            amount: s.amount,
            reference_number: s.reference_number || undefined
          })),
          notes: newPayment.notes || undefined
        });
        toast.success('Split payment recorded successfully');
      } else {
        // Single payment (original logic)
        if (newPayment.payment_method === 'Wallet') {
          if (!clientWallet || clientWallet.balance < newPayment.amount) {
            toast.error(`Insufficient wallet balance. Available: ${formatCurrency(clientWallet?.balance || 0)}`);
            setSaving(false);
            return;
          }
        }
        await createPayment(newPayment);
        toast.success('Payment recorded');
      }
      
      setDialogOpen(false);
      resetPaymentForm();
      loadPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const resetPaymentForm = () => {
    setNewPayment({
      client_id: '',
      invoice_id: '',
      payment_date: new Date().toISOString().split('T')[0],
      amount: 0,
      payment_method: 'Cash',
      reference_number: '',
      bank_name: '',
      notes: '',
      allocate_to_invoices: []
    });
    setClientWallet(null);
    setPaymentMode('single');
    setPaymentSplits([{ payment_method: 'Wallet', amount: 0, reference_number: '' }]);
  };

  // Split payment functions
  const addPaymentSplit = () => {
    setPaymentSplits([...paymentSplits, { payment_method: 'Cash', amount: 0, reference_number: '' }]);
  };

  const removePaymentSplit = (index) => {
    if (paymentSplits.length > 1) {
      setPaymentSplits(paymentSplits.filter((_, i) => i !== index));
    }
  };

  const updatePaymentSplit = (index, field, value) => {
    const updated = [...paymentSplits];
    updated[index] = { ...updated[index], [field]: value };
    setPaymentSplits(updated);
  };

  const getSplitsTotal = () => {
    return paymentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  };

  const getRemainingAmount = () => {
    return newPayment.amount - getSplitsTotal();
  };

  const autoFillWalletAndRemaining = () => {
    if (!clientWallet || clientWallet.balance <= 0) return;
    
    const walletAmount = Math.min(clientWallet.balance, newPayment.amount);
    const remaining = newPayment.amount - walletAmount;
    
    if (remaining > 0) {
      setPaymentSplits([
        { payment_method: 'Wallet', amount: walletAmount, reference_number: '' },
        { payment_method: 'Cash', amount: remaining, reference_number: '' }
      ]);
    } else {
      setPaymentSplits([
        { payment_method: 'Wallet', amount: walletAmount, reference_number: '' }
      ]);
    }
    setPaymentMode('split');
  };

  // Payment Allocation Functions
  const openAllocationSheet = async (payment) => {
    setSelectedPayment(payment);
    setAllocationSheetOpen(true);
    
    try {
      const allocRes = await getPaymentAllocations(payment.id);
      setAllocations(allocRes.data || []);
      
      const invRes = await getInvoices({ client_id: payment.client_id, limit: 100 });
      const unpaid = (invRes.data.items || []).filter(inv => inv.balance_due > 0);
      setAvailableInvoices(unpaid);
      
      const amounts = {};
      unpaid.forEach(inv => {
        amounts[inv.id] = 0;
      });
      setAllocationAmounts(amounts);
    } catch (error) {
      toast.error('Failed to load allocation data');
    }
  };

  const handleAllocate = async (invoiceId) => {
    const amount = allocationAmounts[invoiceId];
    if (!amount || amount <= 0) return;
    
    try {
      await allocatePayment(selectedPayment.id, { invoice_id: invoiceId, amount });
      toast.success('Payment allocated');
      openAllocationSheet(selectedPayment);
      loadPayments();
    } catch (error) {
      toast.error('Failed to allocate');
    }
  };

  const handleRemoveAllocation = async (allocationId) => {
    try {
      await removePaymentAllocation(selectedPayment.id, allocationId);
      toast.success('Allocation removed');
      openAllocationSheet(selectedPayment);
      loadPayments();
    } catch (error) {
      toast.error('Failed to remove allocation');
    }
  };

  const handleDownloadReceipt = async (id, receiptNumber) => {
    setDownloading(id);
    try {
      const res = await getPaymentReceiptPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${receiptNumber || 'receipt'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch (error) {
      toast.error('Failed to download receipt');
    } finally {
      setDownloading(null);
    }
  };

  const getPaymentStatus = (payment) => {
    const allocated = payment.allocated_amount || 0;
    const total = payment.amount || 0;
    
    if (allocated === 0) {
      return { label: 'Unallocated', color: 'bg-amber-100 text-amber-700 ring-1 ring-amber-300' };
    } else if (allocated < total) {
      return { label: 'Partial', color: 'bg-blue-100 text-blue-700 ring-1 ring-blue-300' };
    } else {
      return { label: 'Allocated', color: 'bg-green-100 text-green-700 ring-1 ring-green-300' };
    }
  };

  const canSavePayment = () => {
    if (!newPayment.client_id || !newPayment.amount || newPayment.amount <= 0) {
      return false;
    }
    
    if (paymentMode === 'split') {
      const splitsTotal = getSplitsTotal();
      if (Math.abs(splitsTotal - newPayment.amount) > 0.01) {
        return false;
      }
      
      // Check wallet balance
      const walletSplitAmount = paymentSplits
        .filter(s => s.payment_method === 'Wallet')
        .reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
      
      if (walletSplitAmount > (clientWallet?.balance || 0)) {
        return false;
      }
    } else {
      // Single payment wallet check
      if (newPayment.payment_method === 'Wallet' && newPayment.amount > (clientWallet?.balance || 0)) {
        return false;
      }
    }
    
    return true;
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payments</h1>
          <p className="text-muted-foreground">Record and manage payments</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-payment-btn">
          <Plus className="w-4 h-4 mr-2" />
          Record Payment
        </Button>
      </div>

      <Card>
        <CardHeader>
          <SmartSearch
            data={allPayments}
            columns={searchColumns}
            onFilter={setFilteredPayments}
            quickFilters={quickFilters}
            placeholder="Search by receipt #, client, method, amount..."
          />
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Receipt #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Method</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map((payment) => {
                const status = getPaymentStatus(payment);
                return (
                  <TableRow key={payment.id} data-testid="payment-row">
                    <TableCell>{formatDate(payment.payment_date)}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {payment.payment_number || payment.receipt_number || '-'}
                      {payment.is_split_payment && (
                        <Badge variant="outline" className="ml-2 text-xs">Split</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{payment.client_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={payment.payment_method === 'Wallet' ? 'bg-primary/10 text-primary border-primary/30' : ''}>
                        {payment.payment_method === 'Wallet' && <Wallet className="w-3 h-3 mr-1 inline" />}
                        {payment.payment_method}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {formatCurrency(payment.amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={status.color}>{status.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAllocationSheet(payment)}
                          data-testid={`allocate-btn-${payment.id}`}
                        >
                          <Split className="w-4 h-4 mr-1" />
                          Allocate
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownloadReceipt(payment.id, payment.payment_number)}
                          disabled={downloading === payment.id}
                          data-testid={`download-receipt-${payment.id}`}
                        >
                          <Download className={`w-4 h-4 ${downloading === payment.id ? 'animate-pulse' : ''}`} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredPayments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No payments found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredPayments.length} of {allPayments.length} payments
          </div>
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Client *</Label>
              <Select
                value={newPayment.client_id}
                onValueChange={(v) => handlePaymentChange('client_id', v)}
              >
                <SelectTrigger data-testid="payment-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.trade_name || c.full_name}
                      {c.wallet_balance > 0 && (
                        <span className="ml-2 text-primary text-xs">
                          (Wallet: {formatCurrency(c.wallet_balance)})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Wallet Balance Banner */}
            {clientWallet && clientWallet.balance > 0 && (
              <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Available Wallet Balance</p>
                      <p className="text-lg font-bold text-primary">{formatCurrency(clientWallet.balance)}</p>
                    </div>
                  </div>
                  {newPayment.amount > 0 && clientWallet.balance < newPayment.amount && (
                    <Button size="sm" variant="outline" onClick={autoFillWalletAndRemaining} data-testid="use-wallet-btn">
                      Use Wallet + Other
                    </Button>
                  )}
                </div>
              </div>
            )}

            {clientInvoices.length > 0 && (
              <div>
                <Label>Apply to Invoice (Optional)</Label>
                <Select
                  value={newPayment.invoice_id}
                  onValueChange={(v) => handlePaymentChange('invoice_id', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select invoice or leave blank" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific invoice</SelectItem>
                    {clientInvoices.map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.invoice_number} - Balance: {formatCurrency(inv.balance_due)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Amount *</Label>
                <Input
                  type="number"
                  value={newPayment.amount}
                  onChange={(e) => handlePaymentChange('amount', parseFloat(e.target.value) || 0)}
                  data-testid="payment-amount"
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newPayment.payment_date}
                  onChange={(e) => handlePaymentChange('payment_date', e.target.value)}
                />
              </div>
            </div>

            {/* Payment Mode Toggle */}
            <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
              <Label className="text-sm">Payment Mode:</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={paymentMode === 'single' ? 'default' : 'outline'}
                  onClick={() => setPaymentMode('single')}
                >
                  Single Method
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={paymentMode === 'split' ? 'default' : 'outline'}
                  onClick={() => setPaymentMode('split')}
                  data-testid="split-payment-mode-btn"
                >
                  <Split className="w-4 h-4 mr-1" />
                  Split Payment
                </Button>
              </div>
            </div>

            {paymentMode === 'single' ? (
              /* Single Payment Method */
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Method</Label>
                  <Select
                    value={newPayment.payment_method}
                    onValueChange={(v) => handlePaymentChange('payment_method', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m === 'Wallet' && <Wallet className="w-4 h-4 mr-1 inline" />}
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reference #</Label>
                  <Input
                    value={newPayment.reference_number}
                    onChange={(e) => handlePaymentChange('reference_number', e.target.value)}
                    placeholder="Cheque/Transfer ref"
                    disabled={newPayment.payment_method === 'Wallet'}
                  />
                </div>
              </div>
            ) : (
              /* Split Payment */
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Payment Splits</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addPaymentSplit}>
                    <Plus className="w-4 h-4 mr-1" /> Add Split
                  </Button>
                </div>
                
                {paymentSplits.map((split, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                    <Select
                      value={split.payment_method}
                      onValueChange={(v) => updatePaymentSplit(index, 'payment_method', v)}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentMethods.map((m) => (
                          <SelectItem key={m} value={m}>
                            {m === 'Wallet' && <Wallet className="w-3 h-3 mr-1 inline" />}
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Input
                      type="number"
                      className="w-[120px]"
                      placeholder="Amount"
                      value={split.amount || ''}
                      onChange={(e) => updatePaymentSplit(index, 'amount', parseFloat(e.target.value) || 0)}
                      data-testid={`split-amount-${index}`}
                    />
                    
                    {split.payment_method !== 'Wallet' && (
                      <Input
                        className="flex-1"
                        placeholder="Reference"
                        value={split.reference_number}
                        onChange={(e) => updatePaymentSplit(index, 'reference_number', e.target.value)}
                      />
                    )}
                    
                    {split.payment_method === 'Wallet' && (
                      <span className="text-xs text-muted-foreground flex-1">
                        Available: {formatCurrency(clientWallet?.balance || 0)}
                      </span>
                    )}
                    
                    {paymentSplits.length > 1 && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removePaymentSplit(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                
                {/* Split Summary */}
                <div className="flex items-center justify-between p-2 bg-muted rounded-lg text-sm">
                  <span>Split Total:</span>
                  <span className={`font-mono font-semibold ${Math.abs(getRemainingAmount()) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(getSplitsTotal())}
                    {Math.abs(getRemainingAmount()) > 0.01 && (
                      <span className="ml-2 text-xs">
                        ({getRemainingAmount() > 0 ? `${formatCurrency(getRemainingAmount())} remaining` : `${formatCurrency(Math.abs(getRemainingAmount()))} over`})
                      </span>
                    )}
                  </span>
                </div>
                
                {/* Wallet insufficient warning */}
                {paymentSplits.some(s => s.payment_method === 'Wallet') && (
                  (() => {
                    const walletSplitAmount = paymentSplits
                      .filter(s => s.payment_method === 'Wallet')
                      .reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                    
                    if (walletSplitAmount > (clientWallet?.balance || 0)) {
                      return (
                        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          Wallet amount exceeds balance ({formatCurrency(clientWallet?.balance || 0)} available)
                        </div>
                      );
                    }
                    return null;
                  })()
                )}
              </div>
            )}

            {/* Single payment wallet balance warning */}
            {paymentMode === 'single' && newPayment.payment_method === 'Wallet' && newPayment.client_id && (
              <div className={`p-3 rounded-lg border ${
                clientWallet && clientWallet.balance >= newPayment.amount 
                  ? 'bg-primary/5 border-primary/20' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Wallet Balance</p>
                    <p className={`text-lg font-bold ${
                      clientWallet && clientWallet.balance >= newPayment.amount 
                        ? 'text-primary' 
                        : 'text-red-600'
                    }`}>
                      {formatCurrency(clientWallet?.balance || 0)}
                    </p>
                  </div>
                </div>
                {clientWallet && clientWallet.balance < newPayment.amount && (
                  <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Insufficient balance. Use split payment to combine with another method.
                  </p>
                )}
              </div>
            )}

            <div>
              <Label>Notes</Label>
              <Textarea
                value={newPayment.notes}
                onChange={(e) => handlePaymentChange('notes', e.target.value)}
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetPaymentForm(); }}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={saving || !canSavePayment()} 
                data-testid="save-payment-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Record Payment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Allocation Sheet */}
      <Sheet open={allocationSheetOpen} onOpenChange={setAllocationSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Allocate Payment</SheetTitle>
          </SheetHeader>
          
          {selectedPayment && (
            <div className="space-y-6 mt-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Client</p>
                      <p className="font-medium">{selectedPayment.client_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Amount</p>
                      <p className="font-mono font-semibold">{formatCurrency(selectedPayment.amount)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Method</p>
                      <p>{selectedPayment.payment_method}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Allocated</p>
                      <p className="font-mono">{formatCurrency(selectedPayment.allocated_amount || 0)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {allocations.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Current Allocations</h4>
                  <div className="space-y-2">
                    {allocations.map((alloc) => (
                      <div key={alloc.id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                        <div>
                          <p className="font-mono text-sm">{alloc.invoice_number}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(alloc.amount)}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveAllocation(alloc.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {availableInvoices.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Allocate to Invoices</h4>
                  <div className="space-y-2">
                    {availableInvoices.map((inv) => (
                      <div key={inv.id} className="flex items-center gap-2 p-2 border rounded-lg">
                        <div className="flex-1">
                          <p className="font-mono text-sm">{inv.invoice_number}</p>
                          <p className="text-xs text-muted-foreground">Balance: {formatCurrency(inv.balance_due)}</p>
                        </div>
                        <Input
                          type="number"
                          className="w-24"
                          placeholder="Amount"
                          value={allocationAmounts[inv.id] || ''}
                          onChange={(e) => setAllocationAmounts({
                            ...allocationAmounts,
                            [inv.id]: parseFloat(e.target.value) || 0
                          })}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleAllocate(inv.id)}
                          disabled={!allocationAmounts[inv.id]}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
